import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import EstimateQA from '@/components/EstimateQA';
import { 
  CheckCircle, 
  XCircle, 
  Download,
  Building2,
  Phone,
  Mail,
  MapPin,
  FileSignature,
  Clock,
  Shield,
  X
} from 'lucide-react';

// Separate component to avoid hydration warnings from tooling wrapping dynamic content
const EstimateLineItemsTableBody = ({ items, displayMode }) => (
  <tbody suppressHydrationWarning>
    {items.map((item, index) => (
      <tr key={index} className="border-b border-gray-100 last:border-0" suppressHydrationWarning>
        <td className="py-3 px-4">
          <p className="font-medium">{item.name}</p>
          {item.description && (
            <p className="text-sm text-muted-foreground">{item.description}</p>
          )}
        </td>
        {displayMode !== 'summary' && (
          <>
            <td className="text-right py-3 px-4">{item.qty}</td>
            <td className="text-right py-3 px-4">${item.rate?.toFixed(2)}</td>
          </>
        )}
        <td className="text-right py-3 px-4 font-medium">${item.total?.toFixed(2)}</td>
      </tr>
    ))}
  </tbody>
);

// Sections Display component for combined estimates
const SectionsDisplay = ({ sections, primaryColor, accentColor, token, estimate, onSectionApprovalChange, actionTaken }) => {
  const [selectedSections, setSelectedSections] = useState(
    sections.filter(s => s.is_approved).map(s => s.id)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const canApprove = estimate.status === 'sent' && !actionTaken;
  
  const toggleSection = (sectionId) => {
    if (!canApprove) return;
    setSelectedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };
  
  const selectAll = () => {
    if (!canApprove) return;
    setSelectedSections(sections.map(s => s.id));
  };
  
  const deselectAll = () => {
    if (!canApprove) return;
    setSelectedSections([]);
  };
  
  const selectedTotal = sections
    .filter(s => selectedSections.includes(s.id))
    .reduce((sum, s) => sum + (s.subtotal || 0), 0);
  
  const handleApprove = async () => {
    if (!signerName.trim()) {
      toast.error('Please enter your name to sign');
      return;
    }
    if (!termsAccepted) {
      toast.error('Please accept the terms to continue');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/estimate/${token}/approve-sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_ids: selectedSections,
          approve_all: selectedSections.length === sections.length,
          signer_name: signerName,
          terms_accepted: termsAccepted
        })
      });
      
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to approve');
      }
      
      toast.success(result.message);
      setShowApprovalModal(false);
      
      // Refresh the estimate data
      const refreshResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/estimate/${token}`);
      const refreshData = await refreshResponse.json();
      if (refreshData.estimate) {
        onSectionApprovalChange(refreshData.estimate);
      }
    } catch (error) {
      console.error('Approval error:', error);
      toast.error(error.message || 'Failed to approve sections');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Selection Controls */}
      {canApprove && (
        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl border border-purple-200">
          <p className="text-sm text-purple-800">
            <strong>Select the sections you'd like to approve:</strong>
          </p>
          <div className="flex gap-2">
            <button 
              onClick={selectAll}
              className="text-sm text-purple-600 hover:text-purple-800 underline"
            >
              Select All
            </button>
            <span className="text-purple-300">|</span>
            <button 
              onClick={deselectAll}
              className="text-sm text-purple-600 hover:text-purple-800 underline"
            >
              Clear
            </button>
          </div>
        </div>
      )}
      
      {/* Sections List */}
      {sections.map((section, index) => {
        const isSelected = selectedSections.includes(section.id);
        const wasApproved = section.is_approved;
        
        return (
          <div 
            key={section.id} 
            className={`border-2 rounded-xl overflow-hidden transition-all cursor-pointer ${
              isSelected 
                ? 'border-purple-500 shadow-lg' 
                : wasApproved 
                  ? 'border-green-300 bg-green-50/50' 
                  : 'border-gray-200'
            }`}
            onClick={() => toggleSection(section.id)}
          >
            {/* Section Header */}
            <div 
              className="p-4 flex items-center justify-between"
              style={{ backgroundColor: isSelected ? primaryColor : wasApproved ? '#dcfce7' : '#f8fafc' }}
            >
              <div className="flex items-center gap-3">
                {canApprove && (
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isSelected 
                      ? 'bg-white border-white' 
                      : 'border-gray-300 bg-white'
                  }`}>
                    {isSelected && <CheckCircle className="w-5 h-5" style={{ color: primaryColor }} />}
                  </div>
                )}
                {wasApproved && !canApprove && (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
                <h4 className={`font-bold text-lg ${isSelected ? 'text-white' : wasApproved ? 'text-green-800' : ''}`}>
                  {section.title}
                </h4>
              </div>
              <span className={`font-bold text-xl ${isSelected ? 'text-white' : ''}`} style={{ color: isSelected ? 'white' : primaryColor }}>
                ${(section.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
            
            {/* Section Content */}
            <div className="p-4 bg-white">
              {section.description && (
                <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">{section.description}</p>
              )}
              
              {/* Line Items */}
              {section.line_items?.length > 0 && (
                <div className="space-y-2">
                  {section.line_items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                      <span>{item.name}</span>
                      <span className="text-gray-600">
                        {item.qty} × ${item.rate?.toFixed(2)} = <strong>${((item.qty || 0) * (item.rate || 0)).toFixed(2)}</strong>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
      
      {/* Selected Total & Approve Button */}
      {canApprove && selectedSections.length > 0 && (
        <div className="sticky bottom-4 p-4 bg-white rounded-xl shadow-xl border-2 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{selectedSections.length} section(s) selected</p>
              <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                ${selectedTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <Button
              onClick={(e) => { e.stopPropagation(); setShowApprovalModal(true); }}
              className="h-14 px-8 text-lg"
              style={{ backgroundColor: primaryColor }}
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Approve Selected
            </Button>
          </div>
        </div>
      )}
      
      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowApprovalModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Approve Selected Sections</h3>
            
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-muted-foreground">You are approving:</p>
                <ul className="mt-2 space-y-1">
                  {sections.filter(s => selectedSections.includes(s.id)).map(s => (
                    <li key={s.id} className="flex justify-between text-sm">
                      <span>✓ {s.title}</span>
                      <span className="font-medium">${(s.subtotal || 0).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 pt-2 border-t flex justify-between font-bold">
                  <span>Total</span>
                  <span>${selectedTotal.toFixed(2)}</span>
                </div>
              </div>
              
              <div>
                <Label>Your Full Name (for e-signature)</Label>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="John Smith"
                  className="mt-1"
                />
              </div>
              
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-muted-foreground">
                  I agree to sign this estimate electronically and accept the terms and conditions.
                </span>
              </label>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowApprovalModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={isSubmitting || !signerName.trim() || !termsAccepted}
                  className="flex-1"
                  style={{ backgroundColor: primaryColor }}
                >
                  {isSubmitting ? 'Approving...' : 'Sign & Approve'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Simple Mode Pricing Display with individual approval
const SimplePricingDisplay = ({ estimate, primaryColor, accentColor, token, onOptionsApprovalChange, actionTaken }) => {
  const simplePricing = estimate.simple_pricing || [];
  const [selectedOptions, setSelectedOptions] = useState(
    simplePricing.filter(o => o.is_approved).map(o => o.id || o.title)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const canApprove = estimate.status === 'sent' && !actionTaken;
  const hasMultipleOptions = simplePricing.length > 1;
  
  const toggleOption = (optionId) => {
    if (!canApprove) return;
    setSelectedOptions(prev => 
      prev.includes(optionId) 
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };
  
  const selectAll = () => {
    if (!canApprove) return;
    setSelectedOptions(simplePricing.map(o => o.id || o.title));
  };
  
  const deselectAll = () => {
    if (!canApprove) return;
    setSelectedOptions([]);
  };
  
  const selectedTotal = simplePricing
    .filter(o => selectedOptions.includes(o.id || o.title))
    .reduce((sum, o) => sum + (o.price || 0), 0);
  
  const taxRate = estimate.tax_rate || 0;
  const taxOnSelected = selectedTotal * (taxRate / 100);
  const totalWithTax = selectedTotal + taxOnSelected;
  const depositPercent = estimate.deposit_value || organization?.default_deposit_percent || 50;
  const depositAmount = totalWithTax * (depositPercent / 100);
  
  const handleApprove = async () => {
    if (selectedOptions.length === 0) {
      toast.error('Please select at least one option to approve');
      return;
    }
    if (!signerName.trim()) {
      toast.error('Please enter your name to sign');
      return;
    }
    if (!termsAccepted) {
      toast.error('Please accept the terms to continue');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/estimate/${token}/approve-simple-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          option_ids: selectedOptions,
          approve_all: selectedOptions.length === simplePricing.length,
          signer_name: signerName,
          terms_accepted: termsAccepted
        })
      });
      
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to approve');
      }
      
      toast.success(result.message);
      setShowApprovalModal(false);
      
      // Refresh the estimate data
      const refreshResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/estimate/${token}`);
      const refreshData = await refreshResponse.json();
      if (refreshData.estimate) {
        onOptionsApprovalChange(refreshData.estimate);
      }
    } catch (error) {
      console.error('Approval error:', error);
      toast.error(error.message || 'Failed to approve options');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Scope of Work Section */}
      {(estimate.simple_materials || estimate.simple_process) && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4" style={{ backgroundColor: primaryColor, color: 'white' }}>
            <h4 className="font-bold text-lg">Scope of Work</h4>
          </div>
          <div className="p-5 bg-white space-y-4">
            {estimate.simple_materials && (
              <div>
                <h5 className="font-semibold text-gray-800 mb-2">Materials</h5>
                <div className="text-gray-700 whitespace-pre-line text-sm pl-2 border-l-2" style={{ borderColor: primaryColor }}>
                  {estimate.simple_materials}
                </div>
              </div>
            )}
            {estimate.simple_process && (
              <div>
                <h5 className="font-semibold text-gray-800 mb-2">Process</h5>
                <div className="text-gray-700 whitespace-pre-line text-sm pl-2 border-l-2" style={{ borderColor: primaryColor }}>
                  {estimate.simple_process}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Estimated Duration */}
      {estimate.estimated_duration && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4" style={{ backgroundColor: primaryColor, color: 'white' }}>
            <h4 className="font-bold text-lg">Project Timeline</h4>
          </div>
          <div className="p-5 bg-white">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-600">Estimated Duration:</span>
              <span className="text-gray-800 font-semibold">{estimate.estimated_duration}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Pricing Options */}
      {simplePricing.length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4" style={{ backgroundColor: primaryColor, color: 'white' }}>
            <h4 className="font-bold text-lg">
              {hasMultipleOptions ? 'Cost Breakdown' : 'Total Estimated Cost'}
            </h4>
          </div>
          
          {/* Selection Controls */}
          {canApprove && hasMultipleOptions && (
            <div className="flex items-center justify-between p-3 bg-blue-50 border-b border-gray-200">
              <p className="text-sm text-blue-800">
                <strong>Review each item and select the work you want completed:</strong>
              </p>
              <div className="flex gap-2">
                <button 
                  onClick={selectAll}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Select All
                </button>
                <span className="text-blue-300">|</span>
                <button 
                  onClick={deselectAll}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
          
          <div className="p-5 bg-white">
            <div className="space-y-3">
              {simplePricing.map((item, index) => {
                const optionId = item.id || item.title;
                const isSelected = selectedOptions.includes(optionId);
                const wasApproved = item.is_approved === true;
                const wasDeclined = item.is_approved === false;
                
                return (
                  <div 
                    key={index} 
                    onClick={() => hasMultipleOptions && toggleOption(optionId)}
                    className={`flex justify-between items-center py-3 px-4 rounded-lg border-2 transition-all ${
                      hasMultipleOptions && canApprove ? 'cursor-pointer hover:shadow-md' : ''
                    } ${
                      wasApproved 
                        ? 'border-green-300 bg-green-50' 
                        : wasDeclined 
                          ? 'border-red-200 bg-red-50 opacity-60'
                          : isSelected 
                            ? 'border-blue-500 bg-blue-50 shadow-md' 
                            : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {hasMultipleOptions && canApprove && (
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-500' 
                            : 'border-gray-300 bg-white'
                        }`}>
                          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                      )}
                      <div>
                        <span className={`font-medium ${wasDeclined ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                          {item.title}
                        </span>
                        {wasApproved && (
                          <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            ✓ Approved
                          </span>
                        )}
                        {wasDeclined && (
                          <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            ✗ Declined
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`text-lg font-bold ${wasDeclined ? 'line-through text-gray-400' : ''}`} style={{ color: wasDeclined ? undefined : primaryColor }}>
                      ${(item.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                );
              })}
            </div>
            
            {/* Selected Total Display */}
            {canApprove && selectedOptions.length > 0 && (
              <div className="mt-6 pt-4 border-t-2 border-gray-200">
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Selected Work Total:</span>
                    <span className="font-medium">${selectedTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {taxRate > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax ({taxRate}%):</span>
                      <span className="font-medium">${taxOnSelected.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                    <span>Your Total:</span>
                    <span style={{ color: primaryColor }}>${totalWithTax.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {estimate.require_deposit && (
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Deposit Due ({depositPercent}%):</span>
                      <span className="font-medium">${depositAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
                
                {/* Approve Button */}
                <button
                  onClick={() => setShowApprovalModal(true)}
                  className="w-full mt-4 py-3 px-6 text-white font-bold rounded-xl transition-all hover:shadow-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  {selectedOptions.length === simplePricing.length 
                    ? 'Approve All Work' 
                    : `Approve ${selectedOptions.length} Selected Item${selectedOptions.length > 1 ? 's' : ''}`
                  }
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-xl font-bold" style={{ color: primaryColor }}>Confirm Your Selection</h3>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-2">You're approving the following work:</p>
              <ul className="text-sm space-y-1">
                {simplePricing
                  .filter(o => selectedOptions.includes(o.id || o.title))
                  .map((o, i) => (
                    <li key={i} className="flex justify-between">
                      <span>• {o.title}</span>
                      <span className="font-medium">${o.price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </li>
                  ))
                }
              </ul>
              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between font-bold">
                <span>Total:</span>
                <span style={{ color: primaryColor }}>${totalWithTax.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Name (E-Signature)</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Type your full name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-gray-600">
                I agree to the terms and conditions and authorize this estimate electronically.
              </span>
            </label>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={isSubmitting}
                className="flex-1 py-2 px-4 text-white rounded-lg font-medium disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {isSubmitting ? 'Approving...' : 'Confirm & Sign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const PublicEstimatePage = () => {
  const { token } = useParams();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [actionTaken, setActionTaken] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [eSignConsent, setESignConsent] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [depositInvoiceToken, setDepositInvoiceToken] = useState(null);
  const [depositAmount, setDepositAmount] = useState(0);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    loadEstimate();
  }, [token]);

  // Update current time every second when e-sign modal is open
  useEffect(() => {
    if (showESignModal) {
      const timer = setInterval(() => setCurrentTime(new Date()), 1000);
      return () => clearInterval(timer);
    }
  }, [showESignModal]);

  // Pre-populate signer name when data loads
  useEffect(() => {
    if (data?.client?.name) {
      setSignerName(data.client.name);
    }
  }, [data]);

  const loadEstimate = async () => {
    try {
      const response = await apiHelpers.getPublicEstimate(token);
      setData(response.data);
      
      // Capture deposit invoice token if present (for returning visitors after approval)
      if (response.data.deposit_invoice_token) {
        setDepositInvoiceToken(response.data.deposit_invoice_token);
        const est = response.data.estimate;
        const computed = est.deposit_amount
          || (est.total || 0) * ((est.deposit_value || 50) / 100);
        setDepositAmount(computed);
      }
      
      // Check if already actioned
      if (response.data.estimate.status === 'approved') {
        setActionTaken('approved');
      } else if (response.data.estimate.status === 'declined') {
        setActionTaken('declined');
      }
    } catch (error) {
      console.error('Failed to load estimate:', error);
      toast.error('Unable to load estimate', {
        description: 'This link may be invalid or expired'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    // Show e-sign modal instead of approving directly
    setShowESignModal(true);
    setCurrentTime(new Date());
  };

  const handleESignSubmit = async () => {
    if (!signerName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!eSignConsent) {
      toast.error('Please check the consent box to sign electronically');
      return;
    }
    
    setIsApproving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/estimate/${token}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signer_name: signerName,
          signer_email: data?.client?.email || '',
          terms_accepted: true
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to sign');
      }
      
      const result = await response.json();
      // If a deposit invoice was auto-created, capture its token to show Pay Now button
      if (result.deposit_invoice_token) {
        setDepositInvoiceToken(result.deposit_invoice_token);
        setDepositAmount(result.deposit_amount || 0);
      }

      // 🚀 Approve & Pay: if backend returned a pre-built Stripe Checkout URL, redirect now.
      if (result.checkout_url) {
        toast.success('Approved! Redirecting to secure payment…');
        // Tiny delay so the toast renders, then redirect.
        setTimeout(() => { window.location.href = result.checkout_url; }, 600);
        return;
      }

      setShowESignModal(false);
      setActionTaken('approved');
      // Scroll to top so the customer sees the success banner + Pay Deposit button
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('Estimate Signed & Approved!', {
        description: result.deposit_invoice_token
          ? 'Next step: pay your deposit to schedule work.'
          : 'Thank you! Your electronic signature has been recorded.'
      });
    } catch (error) {
      console.error('Failed to sign:', error);
      toast.error('Failed to sign estimate', {
        description: error.message || 'Please try again'
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleDecline = () => {
    // Open modal to optionally collect a reason
    setDeclineReason('');
    setShowDeclineModal(true);
  };

  const submitDecline = async () => {
    setIsDeclining(true);
    try {
      await apiHelpers.declineEstimate(token, declineReason || null);
      setShowDeclineModal(false);
      setActionTaken('declined');
      toast.success('Estimate Declined', {
        description: 'The contractor has been notified.'
      });
    } catch (error) {
      console.error('Failed to decline:', error);
      toast.error('Failed to decline estimate');
    } finally {
      setIsDeclining(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">Estimate Not Found</h1>
          <p className="text-muted-foreground">
            This link may be invalid or the estimate may have expired.
          </p>
        </div>
      </div>
    );
  }

  const { estimate, organization, client } = data;
  const primaryColor = organization?.brand_primary_color || '#1e3a8a';
  const accentColor = organization?.brand_accent_color || '#fbbf24';

  return (
    <div
      className="min-h-screen pb-12"
      style={{
        background: `linear-gradient(180deg, ${primaryColor}0a 0%, #f8fafc 240px, #f8fafc 100%)`,
      }}
      data-testid="public-estimate-page"
    >
      {/* Sticky top bar */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200/70">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {organization?.logo_url ? (
              <img
                src={organization.logo_url}
                alt={organization.company_name}
                className="h-7 max-w-[90px] object-contain"
              />
            ) : (
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                {organization?.company_name?.charAt(0) || 'C'}
              </div>
            )}
            <span className="font-semibold text-sm truncate" style={{ color: primaryColor }}>
              {organization?.company_name}
            </span>
          </div>
          <span className="text-[11px] uppercase tracking-wider font-bold text-gray-400">
            Estimate
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
        {/* Hero card */}
        <div
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-4"
          data-testid="estimate-hero-card"
        >
          <div className="flex flex-col gap-5">
            {/* Top row: company + estimate badge */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                {organization?.logo_url ? (
                  <img
                    src={organization.logo_url}
                    alt={organization.company_name}
                    className="h-12 sm:h-14 max-w-[140px] object-contain"
                  />
                ) : (
                  <div
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-sm"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {organization?.company_name?.charAt(0) || 'C'}
                  </div>
                )}
                <div>
                  <p className="font-bold text-base sm:text-lg leading-tight" style={{ color: primaryColor }}>
                    {organization?.company_name}
                  </p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                    {organization?.phone && (
                      <a href={`tel:${organization.phone}`} className="flex items-center gap-1 hover:text-gray-700">
                        <Phone className="w-3 h-3" />
                        {organization.phone}
                      </a>
                    )}
                    {organization?.email && (
                      <a href={`mailto:${organization.email}`} className="flex items-center gap-1 hover:text-gray-700">
                        <Mail className="w-3 h-3" />
                        Email
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <span
                className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full flex-shrink-0"
                style={{ background: `${primaryColor}14`, color: primaryColor }}
              >
                <FileSignature className="w-3 h-3" /> Estimate
              </span>
            </div>

            {/* Big amount + meta */}
            <div className="pt-2">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 mb-1">
                Total estimate
              </p>
              <p className="font-bold text-3xl sm:text-4xl tracking-tight" style={{ color: primaryColor }}>
                ${(estimate.total || 0).toFixed(2)}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-700">
                  <span className="font-mono font-semibold">{(estimate.id || '').toUpperCase()}</span>
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-700">
                  <Clock className="w-3 h-3 text-gray-400" />
                  {new Date(estimate.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                {client?.name && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-700">
                    For <span className="font-semibold text-gray-900">{client.name}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Status Banner */}
        {actionTaken && (
          <div 
            className={`rounded-2xl p-4 text-center font-bold text-base mb-4 border ${
              actionTaken === 'approved' 
                ? 'bg-green-50 text-green-700 border-green-200' 
                : 'bg-red-50 text-red-700 border-red-200'
            }`}
          >
            {actionTaken === 'approved' ? (
              <div className="flex flex-col items-center gap-3">
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  This estimate has been approved
                </span>
                {depositInvoiceToken && (
                  <a
                    href={`/invoice/view/${depositInvoiceToken}`}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl text-base font-semibold shadow-md transition-colors"
                    data-testid="pay-deposit-now-link"
                  >
                    Pay Deposit Now &middot; ${(depositAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </a>
                )}
                {depositInvoiceToken && (
                  <p className="text-green-800 text-xs font-normal mt-1">
                    A deposit invoice has been emailed to you. Pay it to schedule and begin work.
                  </p>
                )}
              </div>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <XCircle className="w-6 h-6" />
                This estimate has been declined
              </span>
            )}
          </div>
        )}
        
        {/* Revised Estimate Banner */}
        {!actionTaken && (estimate.revision_number || 0) > 0 && (
          <div className="bg-amber-50 border-y border-amber-200 px-4 py-3 text-center">
            <p className="text-sm font-semibold text-amber-900">
              REVISED ESTIMATE &middot; Revision #{estimate.revision_number}
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              This is an updated version of a previous estimate. Please review the changes.
            </p>
          </div>
        )}
        
        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-7 mb-4">
          {/* Prepared For */}
          <div className="mb-6 p-4 rounded-xl" style={{ background: `${accentColor}10`, borderLeft: `3px solid ${accentColor}` }}>
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Prepared for</p>
            <p className="font-bold text-lg leading-tight">{client?.name}</p>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-gray-600 mt-1">
              {client?.email && <span>{client.email}</span>}
              {client?.phone && <span>· {client.phone}</span>}
            </div>
            {client?.address && <p className="text-xs text-gray-500 mt-1">{client.address}</p>}
          </div>
          
          {/* Job Details */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-1 w-6 rounded-full" style={{ background: primaryColor }} />
              <h3 className="font-bold text-base sm:text-lg" style={{ color: primaryColor }}>
                {estimate.project_title || 'Project details'}
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-gray-50/70 border border-gray-100">
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Service</p>
                <p className="font-semibold text-gray-900 mt-0.5">{estimate.service_type}</p>
              </div>
              {estimate.job_address && (
                <div className="p-3 rounded-lg bg-gray-50/70 border border-gray-100">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Location</p>
                  <p className="font-medium">{estimate.job_address}</p>
                </div>
              )}
            </div>
            {/* Work Types Tags - only show if populated */}
            {estimate.work_types && estimate.work_types.length > 0 && (
              <div className="mt-3">
                <p className="text-muted-foreground text-sm mb-1">Work Includes</p>
                <div className="flex flex-wrap gap-2">
                  {estimate.work_types.map((type, idx) => (
                    <span 
                      key={idx} 
                      className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Before Photos - show site photos to customer, exclude sketches */}
          {estimate.photos && estimate.photos.length > 0 && (
            <div className="mb-8" data-testid="estimate-before-photos">
              <h3 className="font-heading text-lg font-semibold mb-3" style={{ color: primaryColor }}>
                Site Photos
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {estimate.photos.map((photo, idx) => {
                  const photoUrl = typeof photo === 'string' ? photo : (photo.dataUrl || photo.url || '');
                  if (!photoUrl) return null;
                  return (
                    <div key={idx} className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                      <img
                        src={photoUrl}
                        alt={`Site photo ${idx + 1}`}
                        className="w-full h-40 object-cover"
                        loading="lazy"
                      />
                      {photo.tag && (
                        <div className="px-2 py-1 text-xs text-muted-foreground bg-gray-100 text-center capitalize">
                          {photo.tag.replace(/_/g, ' ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Line Items */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-1 w-6 rounded-full" style={{ background: primaryColor }} />
              <h3 className="font-bold text-base sm:text-lg" style={{ color: primaryColor }}>
                Estimate Details
              </h3>
            </div>
            
            {/* Simple Mode Display */}
            {estimate.display_mode === 'simple' && (
              <SimplePricingDisplay 
                estimate={estimate}
                primaryColor={primaryColor}
                accentColor={accentColor}
                token={token}
                onOptionsApprovalChange={(updatedEstimate) => setData({ ...data, estimate: updatedEstimate })}
                actionTaken={actionTaken}
              />
            )}
            
            {/* Summary Mode Display */}
            {estimate.display_mode === 'summary' && estimate.summary_sections?.length > 0 && (
              <div className="space-y-4">
                {estimate.summary_sections.map((section, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="p-4" style={{ backgroundColor: primaryColor, color: 'white' }}>
                      <h4 className="font-bold text-lg">{section.title}</h4>
                    </div>
                    <div className="p-4 bg-white">
                      {section.description && (
                        <div className="mb-4 text-gray-700 whitespace-pre-line text-sm">
                          {section.description}
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                        <span className="font-medium text-gray-600">Price:</span>
                        <span className="text-xl font-bold" style={{ color: primaryColor }}>
                          ${section.price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Combined/Sections Mode Display */}
            {estimate.display_mode === 'sections' && estimate.estimate_sections?.length > 0 && (
              <SectionsDisplay 
                sections={estimate.estimate_sections} 
                primaryColor={primaryColor}
                accentColor={accentColor}
                token={token}
                estimate={estimate}
                onSectionApprovalChange={(updatedEstimate) => setData({ ...data, estimate: updatedEstimate })}
                actionTaken={actionTaken}
              />
            )}
            
            {/* Itemized Mode Display */}
            {estimate.display_mode !== 'summary' && estimate.display_mode !== 'simple' && (
              <>
            {/* Desktop Table */}
            <div className="hidden sm:block rounded-2xl overflow-hidden border border-gray-100 bg-white">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider font-bold text-gray-500">Description</th>
                    <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider font-bold text-gray-500 w-16">Qty</th>
                    <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider font-bold text-gray-500 w-24">Rate</th>
                    <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider font-bold text-gray-500 w-24">Amount</th>
                  </tr>
                </thead>
                <EstimateLineItemsTableBody items={estimate.line_items || []} displayMode={estimate.display_mode} />
              </table>
            </div>
            
            {/* Mobile Card Layout */}
            <div className="sm:hidden space-y-2.5">
              {estimate.line_items?.map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-4 border border-gray-100 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
                  <div className="flex justify-between items-start gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-base text-gray-900">{item.name}</p>
                      {item.description && (
                        <p className="text-sm text-gray-500 mt-0.5 leading-snug">{item.description}</p>
                      )}
                    </div>
                    <p className="font-bold text-lg whitespace-nowrap" style={{ color: primaryColor }}>
                      ${item.total?.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex gap-2 text-xs text-gray-500 pt-2 border-t border-gray-100">
                    <span>Qty {item.qty}</span>
                    <span className="text-gray-300">·</span>
                    <span>${item.rate?.toFixed(2)} each</span>
                  </div>
                </div>
              ))}
            </div>
              </>
            )}
            
            {/* Totals */}
            <div className="mt-5 flex justify-end">
              <div className="w-full sm:w-72">
                <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4 sm:p-5 space-y-2.5">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-medium text-gray-900">${estimate.subtotal?.toFixed(2)}</span>
                  </div>
                  {estimate.tax_amount > 0 && (
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Tax ({estimate.tax_rate}%)</span>
                      <span className="font-medium text-gray-900">${estimate.tax_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="h-px bg-gray-200/80 my-1" />
                  <div className="flex justify-between items-baseline">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Total due</span>
                    <span className="text-2xl sm:text-[28px] font-bold tracking-tight" style={{ color: primaryColor }}>
                      ${estimate.total?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Validity Notice */}
          <div className="mb-6 flex items-center justify-center gap-2 text-xs text-gray-500">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span>This estimate is valid for 30 days from the date above.</span>
          </div>

          {/* Customer Q&A — Adobe-style threaded comments */}
          <div className="mb-6">
            <EstimateQA
              mode="public"
              token={token}
              defaultAuthorName={data?.client?.name || ''}
              defaultAuthorEmail={data?.client?.email || ''}
              primaryColor={primaryColor}
            />
          </div>
          
          {/* Terms & Conditions Section */}
          {!actionTaken && estimate.status === 'sent' && data.terms_and_conditions && (
            <div className="mb-6">
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setShowTerms(!showTerms)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="flex items-center gap-2 font-semibold text-gray-900">
                    <Shield className="w-4 h-4 text-gray-400" />
                    Terms & Conditions
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: primaryColor }}>
                    {showTerms ? 'Hide' : 'View'}
                  </span>
                </button>
                
                {showTerms && (
                  <div className="px-4 pb-4 border-t border-gray-100 max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-600 leading-relaxed pt-3">
                      {data.terms_and_conditions}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          {!actionTaken && estimate.status === 'sent' && (
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                onClick={handleApprove}
                disabled={isApproving || isDeclining}
                className="flex-1 h-14 text-base font-bold rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 bg-green-600 hover:bg-green-700 text-white"
                data-testid="approve-estimate-btn"
              >
                {isApproving ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <FileSignature className="w-5 h-5 mr-2" />
                )}
                Sign & Approve
              </Button>
              
              <Button
                onClick={handleDecline}
                disabled={isApproving || isDeclining}
                variant="outline"
                className="flex-1 h-14 text-base font-semibold rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                data-testid="decline-estimate-btn"
              >
                {isDeclining ? (
                  <span className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <XCircle className="w-5 h-5 mr-2" />
                )}
                Decline
              </Button>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="text-center text-xs text-gray-400 mt-6 pb-2 px-4">
          <p>Thank you for considering <span className="font-semibold text-gray-600">{organization?.company_name}</span></p>
          {(organization?.phone || organization?.email) && (
            <p className="mt-1">
              Questions? {organization?.phone || organization?.email}
            </p>
          )}
        </div>
      </div>

      {/* E-Sign Modal */}
      {showESignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh]">
            {/* Modal Header - Fixed */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6 text-white flex-shrink-0 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileSignature className="w-6 h-6 sm:w-8 sm:h-8" />
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold">Electronic Signature</h2>
                    <p className="text-blue-100 text-xs sm:text-sm">Sign to approve this estimate</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowESignModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Body - Scrollable */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
              {/* Document Info */}
              <div className="bg-gray-50 rounded-xl p-3 sm:p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Document:</span>
                  <span className="font-medium">Estimate #{estimate?.id?.slice(-8)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-bold text-green-600">${estimate?.total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">From:</span>
                  <span className="font-medium">{organization?.company_name}</span>
                </div>
              </div>
              
              {/* Signer Name */}
              <div className="space-y-2">
                <Label htmlFor="signer-name" className="text-sm font-medium">
                  Your Full Legal Name
                </Label>
                <Input
                  id="signer-name"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter your full name"
                  className="h-11 sm:h-12 text-base sm:text-lg"
                  data-testid="esign-name-input"
                />
              </div>
              
              {/* Timestamp */}
              <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-3 sm:p-4">
                <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-blue-600 font-medium">Signing Date & Time</p>
                  <p className="text-xs sm:text-sm font-bold text-blue-800 break-words">
                    {currentTime.toLocaleString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              
              {/* Consent Checkbox */}
              <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-3 sm:p-4">
                <input
                  type="checkbox"
                  id="esign-consent"
                  checked={eSignConsent}
                  onChange={(e) => setESignConsent(e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                  data-testid="esign-consent-checkbox"
                />
                <label htmlFor="esign-consent" className="text-xs sm:text-sm text-gray-700">
                  I agree to sign this estimate electronically. I understand that my electronic signature 
                  is legally binding and has the same effect as a handwritten signature. By signing, 
                  I accept the terms and conditions of this estimate.
                </label>
              </div>
              
              {/* Security Note */}
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span>Your signature is secured and encrypted.</span>
              </div>
            </div>
            
            {/* Modal Footer - Fixed */}
            <div className="p-4 sm:p-6 bg-gray-50 border-t flex gap-3 flex-shrink-0 rounded-b-2xl">
              <Button
                variant="outline"
                onClick={() => setShowESignModal(false)}
                className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
                disabled={isApproving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleESignSubmit}
                disabled={isApproving || !signerName.trim() || !eSignConsent}
                className="flex-1 h-11 sm:h-12 bg-green-600 hover:bg-green-700 text-white font-bold text-sm sm:text-base"
                data-testid="esign-submit-btn"
              >
                {isApproving ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <FileSignature className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                )}
                Sign & Approve
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-gray-900">Decline this estimate?</h3>
                <p className="text-xs text-muted-foreground">The contractor will be notified.</p>
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g. Price is too high, found another contractor, timing doesn't work..."
              className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:border-primary min-h-[100px] resize-y"
              data-testid="decline-reason-textarea"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">{declineReason.length}/500</p>
            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowDeclineModal(false)}
                disabled={isDeclining}
                className="flex-1 rounded-xl"
              >
                Go Back
              </Button>
              <Button
                onClick={submitDecline}
                disabled={isDeclining}
                className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-decline-btn"
              >
                {isDeclining ? 'Declining...' : 'Decline Estimate'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirm Dialog */}
      {ConfirmDialogComponent}
    </div>
  );
};

export default PublicEstimatePage;
